import { useCallback } from 'react'

import { ChatParticipant } from '@tribeplatform/gql-client/types'
import { useRemoveChatParticipants } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { T, useI18n } from '../../i18n/index.js'

import { ModalMemberDetails } from './ModalMemberDetails.js'

interface Props {
  open: boolean
  participant: ChatParticipant
  onClose: () => void
}

export const RemoveModal = ({ open, onClose, participant }: Props) => {
  const { mutateAsync: removeParticipants, isLoading } =
    useRemoveChatParticipants()
  const { $t } = useI18n()

  const onSubmit = useCallback(async () => {
    try {
      await removeParticipants({
        chatId: participant.chatId,
        input: {
          memberIds: [participant.memberId],
        },
      })
      toast({
        title: $t({
          id: 'Generics.Success',
          defaultMessage: 'Success',
        }),
        description: $t({
          id: 'Messaging.Group.MemberRemoved.Success',
          defaultMessage: 'The group member has been removed',
        }),
        status: 'success',
      })
      onClose()
    } catch (error) {
      toast({
        title: $t({
          id: 'Generics.Error',
          defaultMessage: 'Error',
        }),
        description: $t({
          id: 'Messaging.Group.MemberRemoved.Error',
          defaultMessage: 'Failed to remove the group member',
        }),
        status: 'error',
      })
    }
  }, [
    $t,
    onClose,
    participant.chatId,
    participant.memberId,
    removeParticipants,
  ])

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header
        title={
          <div className="flex items-center gap-2">
            <T
              defaultMessage="Remove {member_name} from the group?"
              id="Messaging.RemoveModal.Title"
              values={{
                member_name: participant.name,
              }}
            />
          </div>
        }
        description={
          <T
            defaultMessage="{member_name} will no longer be able to see messages from this group."
            id="Messaging.RemoveModal.Description"
            values={{
              member_name: (
                <span className="font-bold">{participant.name}</span>
              ),
            }}
          />
        }
      />
      <Modal.Content>
        <ModalMemberDetails participant={participant} />
        <Modal.Actions className="!p-0">
          <Button destructive onClick={onSubmit} loading={isLoading}>
            <T defaultMessage="Remove" id="Generics.Remove" />
          </Button>
        </Modal.Actions>
      </Modal.Content>
    </Modal>
  )
}
